import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useBanners } from '@/store/dataStore';

export default function BannerCarousel() {
  const { banners: raw } = useBanners();
  // Storefront must only show explicitly active banners. Server /banners
  // already filters this, but the /admin/banners path (used when a token is
  // present) returns everything — so we double-guard on the client.
  const banners = React.useMemo(
    () => (raw || []).filter((b) => b.active === true || b.active === undefined && !b.id?.startsWith('b_')),
    [raw]
  );
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (banners.length <= 1) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % banners.length), 5000);
    return () => clearInterval(t);
  }, [banners.length]);

  if (!banners.length) return null;

  const raw0 = banners[idx];
  // Normalise so old (bg/cta/href/textColor) and new (bg/cta_text/cta_link/text_color) shapes both render.
  const b = {
    ...raw0,
    bg: raw0.bg || '#F8D13F',
    textColor: raw0.textColor || raw0.text_color || '#1B2A4A',
    cta: raw0.cta || raw0.cta_text || '',
    href: raw0.href || raw0.cta_link || '',
  };
  const prev = () => setIdx((i) => (i - 1 + banners.length) % banners.length);
  const next = () => setIdx((i) => (i + 1) % banners.length);

  return (
    <section className="max-w-7xl mx-auto px-4 pt-4">
      <div className="relative rounded-2xl overflow-hidden shadow-lg" style={{ backgroundColor: b.bg }}>
        <div className="grid md:grid-cols-2 min-h-[220px] md:min-h-[280px]">
          <div className="p-6 md:p-10 flex flex-col justify-center" style={{ color: b.textColor }}>
            <p className="text-xs font-bold uppercase tracking-widest opacity-80">Promoción</p>
            <h2 className="text-3xl md:text-4xl font-extrabold mt-2 leading-tight">{b.title}</h2>
            <p className="mt-2 text-base md:text-lg opacity-90">{b.subtitle}</p>
            {b.cta && b.href && (
              <Link to={b.href} className="inline-block mt-5 w-fit">
                <span className="inline-block px-5 py-2.5 rounded-full font-bold text-sm shadow-md hover:scale-105 transition-transform" style={{ backgroundColor: b.textColor, color: b.bg }}>
                  {b.cta}
                </span>
              </Link>
            )}
          </div>
          <div className="relative min-h-[160px] md:min-h-0">
            {b.image && <img src={b.image} alt={b.title} loading="eager" fetchpriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />}
            <div className="absolute inset-0 bg-gradient-to-l" style={{ backgroundImage: `linear-gradient(to left, transparent, ${b.bg}00 50%, ${b.bg} 100%)` }} />
          </div>
        </div>

        {banners.length > 1 && (
          <>
            <button onClick={prev} className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 hover:bg-white flex items-center justify-center shadow">
              <ChevronLeft size={18} className="text-[#1B2A4A]" />
            </button>
            <button onClick={next} className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-white/80 hover:bg-white flex items-center justify-center shadow">
              <ChevronRight size={18} className="text-[#1B2A4A]" />
            </button>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
              {banners.map((_, i) => (
                <button key={i} onClick={() => setIdx(i)} className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-6 bg-white' : 'w-2 bg-white/50'}`} />
              ))}
            </div>
          </>
        )}
      </div>
    </section>
  );
}
